import {
  Camera,
  CheckCircle,
  Hash,
  Loader2,
  RefreshCw,
  Video,
  VideoOff,
  XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { useMutation } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useGuardContext } from '@/hooks/useGuardContext';
import { useQrScanner } from '@/hooks/useQrScanner';
import { useTranslation } from '@/hooks/useTranslation';
import { validateQrToken, confirmEntry } from '@/services/api.client';
import type { PinValidationResult } from '@/types';

/**
 * QR Scanner Section — camera-based QR code scanning for visitor validation.
 *
 * Streams the device camera into a <video> element, continuously decodes
 * QR codes, and validates the extracted token against POST /guard/qr/validate.
 *
 * Only rendered when GUARD_WEB_INVITES module is active.
 * Requirements: guard-web-app §8 (QR validation via camera)
 */
export function QrScannerSection() {
  const { t } = useTranslation();
  const { gateId, isOnline } = useGuardContext();

  const [result, setResult] = useState<PinValidationResult | null>(null);
  const [actualPartySize, setActualPartySize] = useState<number>(1);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  // Tracks whether the browser has permanently blocked camera access.
  // 'denied' = permanently blocked (no prompt will appear on retry).
  // null = unknown (Permissions API not supported) or not yet queried.
  const [cameraPermState, setCameraPermState] = useState<PermissionState | null>(null);

  const validateMutation = useMutation({
    mutationFn: (token: string) => validateQrToken(token, gateId || undefined),
    onSuccess: (data: PinValidationResult) => {
      setResult(data);
      setActualPartySize(data.partySize ?? 1);
    },
    onError: () => {
      scanner.reset();
    },
  });

  const confirmMutation = useMutation({
    mutationFn: (token: string) =>
      confirmEntry(token, actualPartySize > 1 ? actualPartySize : undefined),
    onSuccess: () => {
      setConfirmError(null);
      handleReset();
    },
    onError: (err: { message?: string }) => {
      setConfirmError(err?.message ?? t('common.error'));
    },
  });

  const handleDecode = useCallback(
    (token: string) => {
      validateMutation.mutate(token);
    },
    [validateMutation],
  );

  const scanner = useQrScanner(handleDecode);

  // When the scanner reaches error-permission, query the Permissions API to
  // distinguish "was denied once but will prompt again" from "permanently blocked".
  // Fall back gracefully when the API is unavailable (older browsers, Firefox).
  useEffect(() => {
    if (scanner.state !== 'error-permission') return;
    if (!navigator.permissions?.query) return;
    navigator.permissions
      .query({ name: 'camera' as PermissionName })
      .then((status) => {
        setCameraPermState(status.state);
        // Update if the user changes the setting while the screen is open
        status.onchange = () => setCameraPermState(status.state);
      })
      .catch(() => {
        /* API available but camera descriptor not supported */
      });
  }, [scanner.state]);

  function handleReset() {
    setResult(null);
    setConfirmError(null);
    validateMutation.reset();
    confirmMutation.reset();
    scanner.reset();
  }

  function handleAllowEntry() {
    if (result?.visitId) {
      confirmMutation.mutate(result.visitId);
    }
  }

  if (!isOnline) {
    return (
      <div className="rounded-[var(--radius-lg)] border border-[var(--status-amber)] bg-[var(--status-amber-tint)] p-4 text-center">
        <p className="text-sm text-[var(--status-amber-text)]">{t('guard.pin.offlineMessage')}</p>
      </div>
    );
  }

  if (result?.result === 'allow') {
    return (
      <div className="space-y-4 rounded-[var(--radius-lg)] border-2 border-[var(--status-green)] bg-[var(--status-green-tint)] p-6">
        <div className="flex items-center justify-center gap-2">
          <CheckCircle className="size-8 text-[var(--status-green-text)]" />
          <h3 className="text-lg font-semibold text-[var(--status-green-text)]">
            {t('guard.pin.granted')}
          </h3>
        </div>
        <div className="space-y-2 text-center text-sm">
          {result.visitor && (
            <p className="font-medium text-[var(--text-primary)]">{result.visitor.name}</p>
          )}
          {result.host && (
            <p className="text-[var(--text-secondary)]">
              {result.host.name} · {result.host.unit}
            </p>
          )}
          {result.invitationType && (
            <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">
              {result.invitationType.replace(/_/g, ' ')}
            </p>
          )}
          {result.validFrom && result.validUntil && (
            <p className="text-xs text-[var(--text-secondary)]">
              {new Date(result.validFrom).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })}
              {' – '}
              {new Date(result.validUntil).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
          )}
          {result.vehicleDetails && (
            <p className="text-xs text-[var(--text-secondary)]">{result.vehicleDetails}</p>
          )}
          {result.partySize && result.partySize > 1 && (
            <div className="mt-2 space-y-1">
              <p className="text-xs font-medium text-[var(--text-primary)]">
                {`${result.partySize} ${t('visitor.partySize.label').toLowerCase()}`}
              </p>
              <div className="flex items-center justify-center gap-2">
                <label
                  htmlFor="qr-actual-party-size"
                  className="text-xs text-[var(--text-secondary)]"
                >
                  {t('visitor.partySize.actual')}:
                </label>
                <Input
                  id="qr-actual-party-size"
                  type="number"
                  min={1}
                  value={actualPartySize}
                  onChange={(e) => setActualPartySize(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-16 min-h-[36px] text-center"
                />
              </div>
            </div>
          )}
        </div>
        {confirmError && (
          <p className="text-xs text-[var(--text-error)]" role="alert">
            {confirmError}
          </p>
        )}
        <div className="flex gap-3">
          <Button variant="outline" className="min-h-[44px] flex-1" onClick={handleReset}>
            {t('common.cancel')}
          </Button>
          <Button
            className="min-h-[44px] flex-1 bg-[var(--status-green)] hover:bg-[var(--status-green)]/90"
            onClick={handleAllowEntry}
            disabled={confirmMutation.isPending}
          >
            {confirmMutation.isPending && <RefreshCw className="mr-2 size-4 animate-spin" />}
            {t('guard.pin.allowEntry')}
          </Button>
        </div>
      </div>
    );
  }

  if (result?.result === 'deny') {
    return (
      <div className="space-y-4 rounded-[var(--radius-lg)] border-2 border-[var(--status-red)] bg-[var(--status-red-tint)] p-6">
        <div className="flex items-center justify-center gap-2">
          <XCircle className="size-8 text-[var(--status-red-text)]" />
          <h3 className="text-lg font-semibold text-[var(--status-red-text)]">
            {t('guard.pin.denied')}
          </h3>
        </div>
        {result.reason && (
          <p className="text-center text-sm text-[var(--text-primary)]">{result.reason}</p>
        )}
        {result.guardMessage && (
          <p className="text-center text-xs text-[var(--text-secondary)]">{result.guardMessage}</p>
        )}
        <Button variant="outline" className="min-h-[44px] w-full" onClick={handleReset}>
          {t('guard.qr.scanAgain')}
        </Button>
      </div>
    );
  }

  if (scanner.state === 'error-permission') {
    // cameraPermState === 'denied' means the browser has permanently blocked the camera.
    // A retry will only produce another immediate rejection — show guidance instead.
    const isPermanentlyBlocked = cameraPermState === 'denied';
    return (
      <div className="space-y-3 rounded-[var(--radius-lg)] border border-[var(--status-amber)] bg-[var(--status-amber-tint)] p-6 text-center">
        <VideoOff className="mx-auto size-8 text-[var(--status-amber-text)]" />
        <p className="text-sm font-medium text-[var(--status-amber-text)]">
          {isPermanentlyBlocked
            ? t('guard.qr.cameraPermissionBlocked')
            : t('guard.qr.cameraPermissionDenied')}
        </p>
        {isPermanentlyBlocked ? (
          // Camera is permanently blocked — direct guard to browser settings.
          // We can't open the settings page programmatically (browser security restriction),
          // so we render this as a <details> with step-by-step instructions.
          // A Retry button is shown below the instructions so the guard can retry
          // in-place after following the steps, without needing to reload the page.
          <>
            <details className="text-left text-xs text-[var(--text-secondary)]">
              <summary className="cursor-pointer font-medium text-[var(--text-accent)]">
                {t('guard.qr.openBrowserSettings')}
              </summary>
              <ol className="mt-2 space-y-1 pl-4 list-decimal">
                <li>{t('guard.qr.settingsStep1')}</li>
                <li>{t('guard.qr.settingsStep2')}</li>
                <li>{t('guard.qr.settingsStep3')}</li>
                <li>{t('guard.qr.settingsStep4')}</li>
              </ol>
            </details>
            <Button
              variant="outline"
              className="min-h-[44px] w-full"
              onClick={() => void scanner.start()}
            >
              {t('common.retry')}
            </Button>
          </>
        ) : (
          <Button variant="outline" className="min-h-[44px]" onClick={() => void scanner.start()}>
            {t('common.retry')}
          </Button>
        )}
      </div>
    );
  }

  if (scanner.state === 'error-no-camera') {
    return (
      <div className="space-y-3 rounded-[var(--radius-lg)] border border-[var(--status-red)] bg-[var(--status-red-tint)] p-6 text-center">
        <VideoOff className="mx-auto size-8 text-[var(--status-red-text)]" />
        <p className="text-sm font-medium text-[var(--text-primary)]">{t('guard.qr.noCamera')}</p>
        <p className="text-xs text-[var(--text-secondary)]">{t('guard.qr.noCameraHint')}</p>
      </div>
    );
  }

  if (scanner.state === 'error-generic') {
    return (
      <div className="space-y-3 rounded-[var(--radius-lg)] border border-[var(--border-error)] bg-[var(--status-red-tint)] p-6 text-center">
        <p className="text-sm text-[var(--text-error)]">
          {scanner.errorMessage ?? t('common.error')}
        </p>
        <Button variant="outline" className="min-h-[44px]" onClick={() => void scanner.start()}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  if (scanner.state === 'idle') {
    return (
      <div className="space-y-3 rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--bg-surface)] p-4">
        <div className="flex items-center gap-2">
          <Camera className="size-5 text-[var(--text-accent)]" />
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">
            {t('guard.qr.title')}
          </h3>
        </div>
        <Button className="min-h-[44px] w-full" onClick={() => void scanner.start()}>
          <Video className="mr-2 size-4" />
          {t('guard.qr.startCamera')}
        </Button>
      </div>
    );
  }

  if (scanner.state === 'requesting-permission') {
    return (
      <div className="space-y-3 rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--bg-surface)] p-6 text-center">
        <Loader2 className="mx-auto size-8 animate-spin text-[var(--text-accent)]" />
        <p className="text-sm text-[var(--text-secondary)]">{t('guard.qr.requestingPermission')}</p>
      </div>
    );
  }

  // Scanning or paused — camera is active
  return (
    <div className="space-y-3 rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--bg-surface)] p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Camera className="size-5 text-[var(--text-accent)]" />
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">
            {t('guard.qr.title')}
          </h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="min-h-[44px] text-xs text-[var(--text-secondary)]"
          onClick={scanner.stop}
        >
          <VideoOff className="mr-1 size-3" />
          {t('guard.qr.stopCamera')}
        </Button>
      </div>

      <div className="relative overflow-hidden rounded-[var(--radius-md)] bg-black aspect-video">
        {/* Video element is muted by design — QR scanner does not use audio */}
        <video
          ref={scanner.videoRef}
          className="h-full w-full object-cover"
          playsInline
          muted
          aria-label={t('guard.qr.videoLabel')}
        />

        {scanner.state === 'scanning' && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="relative h-48 w-48">
              <span className="absolute left-0 top-0 h-6 w-6 border-l-2 border-t-2 border-[var(--text-accent)]" />
              <span className="absolute right-0 top-0 h-6 w-6 border-r-2 border-t-2 border-[var(--text-accent)]" />
              <span className="absolute bottom-0 left-0 h-6 w-6 border-b-2 border-l-2 border-[var(--text-accent)]" />
              <span className="absolute bottom-0 right-0 h-6 w-6 border-b-2 border-r-2 border-[var(--text-accent)]" />
            </div>
          </div>
        )}

        {validateMutation.isPending && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60">
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="size-8 animate-spin text-white" />
              <p className="text-sm text-white">{t('guard.qr.processing')}</p>
            </div>
          </div>
        )}
      </div>

      <p className="text-center text-xs text-[var(--text-secondary)]">
        {scanner.state === 'scanning' ? t('guard.qr.scanning') : t('guard.qr.paused')}
      </p>

      {validateMutation.error && (
        <div className="flex items-center justify-between rounded border border-[var(--border-error)] bg-[var(--status-red-tint)] px-3 py-2">
          <p className="text-xs text-[var(--text-error)]">
            {(validateMutation.error as { message?: string })?.message ?? t('common.error')}
          </p>
          <Button variant="ghost" size="sm" className="min-h-[36px] text-xs" onClick={handleReset}>
            <Hash className="mr-1 size-3" />
            {t('guard.qr.tryPin')}
          </Button>
        </div>
      )}
    </div>
  );
}
